import {
    useMemo,
    useEffect,
    useState,
    useCallback
} from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Divider,
    Link,
    Typography
} from '@mui/material';
import axios from 'axios';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GET_CLASS_INFO_PATH } from '../../config/apiPaths';
import { APPLE_SUPPORT } from '../../constants/urls';
import { useUserAgent } from '../../hooks/useUserAgent';
import {
    desktopLaunch,
    iOSLaunch,
    useDownloadUrl
} from '../../utils/launchApps';
import { getUrls } from '../../utils/url';
import { getEnvironment } from '../../utils/environment';

const Container = styled.div`
    display: flex;
    flex: 1;
    gap: 20px;
    max-height: 100%;
    max-width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
`;

const AppAccordion = styled(Accordion)`
    max-width: 600px;
`;

const IndentedLink = styled(Link)`
    margin-left: 20px;
`;
const StyledTypography = styled(Typography)`
    margin-top: 20px;
`;

const AppLauncher = (props) => {
    const errTrans = useTranslation('error_messages');
    const classTrans = useTranslation('class');
    const { api } = getUrls();
    const { isMac, isWindows, isIOS, isIPad } = useUserAgent(navigator.userAgent);
    const { environment } = getEnvironment();
    const isHipaa = environment === 'hipaa';
    const [joinUrl, setJoinUrl] = useState<string>();
    const [password, setPassword] = useState<string>('');
    const [meetingId, setMeetingId] = useState<string>('');
    const {
        classUuid,
        code,
        userName,
        studentId,
        zak
    } = props;

    useEffect(() => {
        if (isUndefined(classUuid)) {
            return;
        }

        const fetchData = async () => {
            const classInfoUrl = new URL(`${api}${GET_CLASS_INFO_PATH}?class_uuid=${classUuid}`);

            if (!isUndefined(code)) {
                classInfoUrl.searchParams.append('code', code);
            }

            if (!isUndefined(userName)) {
                classInfoUrl.searchParams.append('username', userName);
            }

            if (!isUndefined(studentId)) {
                classInfoUrl.searchParams.append('student_id', studentId);
            }

            const { data } = await axios.get(classInfoUrl.href);
            if (data?.meeting?.data) {
                setJoinUrl(data.meeting.data.join_url);
                setPassword(data.meeting.data.encrypted_password);
                setMeetingId(data.meeting.meeting_id);
            }
        };

        fetchData();
    }, [api, classUuid, code, userName, studentId]);

    const supportedOS = useMemo(() => isMac || isIPad || isWindows, [isMac, isIPad, isWindows]);

    const zoomLink = useMemo(() => {
        if (!joinUrl) {
            return '';
        }
        const zoomUrl = new URL(decodeURIComponent(joinUrl));
        if (zak) {
            zoomUrl.searchParams.append('zak', zak);
        }
        return zoomUrl.href;
    }, [joinUrl, zak]);

    const {
        macNextGenUrl,
        appDownloadUrl
    } = useDownloadUrl();

    const launchApp = useCallback(() => {
        if (isIOS || isIPad) {
            iOSLaunch({ meetingId, password, ...props });
            return;
        }

        desktopLaunch({ meetingId, password, ...props });
    }, [meetingId, password, isIOS, isIPad, props]);

    const renderLaunchClassButton = () => {
        if (!supportedOS) {
            return null;
        }

        if (isHipaa && !isWindows) {
            return null;
        }

        return (
            <Button variant='contained' color='primary' onClick={launchApp} data-testid='launch-class-app'>
                <Typography variant='body1'>{errTrans.t('error_messages:browser-warning:launch-class-app')}</Typography>
            </Button>
        );
    };

    const renderMacDownloadOptions = () => {
        if (!isMac) {
            return null;
        }

        if (isHipaa) {
            return null;
        }

        return (
            <AppAccordion data-testid='mac-app-download-dropdown'>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='body1'>{classTrans.t('class:have-app-2')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <StyledTypography variant='body2'>
                        {classTrans.t('class:download-class-3')}:
                        <IndentedLink href={macNextGenUrl} target='_blank' data-testid='mac-download-btn1'>{classTrans.t('class:download-macos-11')} </IndentedLink>
                    </StyledTypography>
                    <Link href={APPLE_SUPPORT} target='_blank' data-testid='help-btn'>
                        <StyledTypography variant='body2'>{classTrans.t('class:download-macos-find-version')}</StyledTypography>
                    </Link>
                    {joinUrl && (
                        <div data-testid='mac-join-via-zoom'>
                            <Divider sx={{ margin: '13px 0px' }} ></Divider>
                            <StyledTypography variant='body2'>
                                {errTrans.t('error_messages:browser-warning:regular-zoom')}:
                                <IndentedLink href={zoomLink} target='_blank'>{errTrans.t('error_messages:browser-warning:launch-zoom')} </IndentedLink>
                            </StyledTypography>
                        </div>
                    )}
                </AccordionDetails>
            </AppAccordion>
        );
    };

    const renderNonMacDownloadOptions = () => {
        if (isMac || isHipaa) {
            return null;
        }

        return (
            <div style={{ display: 'contents' }} data-testid='nonmac-app-download'>
                {supportedOS && (
                    <StyledTypography variant='body2'>
                        {classTrans.t('class:have-app')}:
                        <IndentedLink href={appDownloadUrl} target='_blank' data-testid='nonmac-app-download-btn'>{classTrans.t('class:download-class')} </IndentedLink>
                    </StyledTypography>
                )}
                {joinUrl && (
                    <StyledTypography variant='body2' data-testid='nonmac-join-via-zoom'>
                        {errTrans.t('error_messages:browser-warning:regular-zoom')}:
                        <IndentedLink href={zoomLink} target='_blank'>{errTrans.t('error_messages:browser-warning:launch-zoom')} </IndentedLink>
                    </StyledTypography>
                )}
            </div>
        );
    };

    const renderHipaaZoomOption = () => {
        if (!isHipaa) {
            return null;
        }

        if (!joinUrl) {
            return null;
        }

        if (isMac) {
            return (
                <div data-testid='mac-join-via-zoom'>
                    <StyledTypography variant='body2'>
                        {errTrans.t('error_messages:browser-warning:regular-zoom')}:
                        <IndentedLink href={zoomLink} target='_blank'>{errTrans.t('error_messages:browser-warning:launch-zoom')} </IndentedLink>
                    </StyledTypography>
                </div>
            );
        }
        return (
            <StyledTypography variant='body2' data-testid='nonmac-join-via-zoom'>
                {errTrans.t('error_messages:browser-warning:regular-zoom')}:
                <IndentedLink href={zoomLink} target='_blank'>{errTrans.t('error_messages:browser-warning:launch-zoom')} </IndentedLink>
            </StyledTypography>
        );
    };

    return (
        <Container data-testid='app-launcher'>
            {renderLaunchClassButton()}
            {renderMacDownloadOptions()}
            {renderNonMacDownloadOptions()}
            {renderHipaaZoomOption()}
        </Container>
    );
};
export default AppLauncher;
